import React, { useState, useMemo } from 'react';

export default function AddAdvisorModal({
  role,
  roleLabel,
  busy = false,
  error = '',
  onClose,
  onSubmit, // ({ role, name, email, phone, firm, send_invite }) => Promise<void>
}) {
  const [name,       setName]       = useState('');
  const [email,      setEmail]      = useState('');
  const [phone,      setPhone]      = useState('');
  const [firm,       setFirm]       = useState('');
  const [sendInvite, setSendInvite] = useState(false);

  const canInvite = useMemo(() => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()), [email]);
  const canSubmit = name.trim().length > 0 && !busy;

  const handleSave = () => {
    if (!canSubmit) return;
    // status: 'invited' triggers the invite mailer in the existing
    // Mutations::AdvisoryTeam::AddMember server-side. status: 'added'
    // is the silent record.
    onSubmit({
      role,
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      firm: firm.trim() || null,
      status: sendInvite && canInvite ? 'invited' : 'added',
    });
  };

  return (
    <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-[16px] p-6 max-w-md w-full space-y-5">
        <div>
          <div className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-1">
            ADD {roleLabel?.toUpperCase() || role}
          </div>
          <h2 className="text-[18px] font-semibold text-foreground mb-1">
            Add to your succession team
          </h2>
          <p className="text-[13px] text-muted-foreground">
            Record the advisor you already work with. We'll add them to your team — no marketplace transaction.
          </p>
        </div>

        <Field label="Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            autoFocus
            className="w-full bg-background border border-input rounded-lg px-3 py-2 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
          />
        </Field>

        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={200}
            className="w-full bg-background border border-input rounded-lg px-3 py-2 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={50}
              className="w-full bg-background border border-input rounded-lg px-3 py-2 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
            />
          </Field>
          <Field label="Firm">
            <input
              type="text"
              value={firm}
              onChange={(e) => setFirm(e.target.value)}
              maxLength={200}
              className="w-full bg-background border border-input rounded-lg px-3 py-2 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
            />
          </Field>
        </div>

        <label className={`flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${!canInvite ? 'opacity-60 cursor-not-allowed hover:bg-transparent' : ''}`}>
          <input
            type="checkbox"
            checked={sendInvite && canInvite}
            disabled={!canInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
            className="w-4 h-4 mt-0.5"
          />
          <div className="flex-1">
            <div className="text-[13px] font-medium text-foreground">Send them an invitation</div>
            <div className="text-[12px] text-muted-foreground">
              {canInvite
                ? "We'll email them an invite to join your succession team on Succeedo."
                : 'Add an email above to enable invitations.'}
            </div>
          </div>
        </label>

        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-[13px] text-destructive">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="border border-border text-foreground rounded-full px-5 py-2 text-[14px] hover:bg-accent transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSubmit}
            className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? 'Saving…' : (sendInvite && canInvite ? 'Save and invite' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required = false, children }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-1.5">
        {label}{required && <span className="text-destructive ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
